import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import tesoreriaCajaService, {
  ArqueoPorTipoPago,
  MovimientoCaja,
  esMovimientoTraspaso,
  formatCajaPlanillaLabel,
} from '../../services/tesoreriaCajaService';
import { formatCurrency, formatDate, formatComprobanteCajaConTipo } from '../../utils/formatters';

const signedImporteDisplay = (mov: MovimientoCaja): number => {
  const raw = Number(mov.importe);
  if (!Number.isFinite(raw)) return 0;
  if (mov.tipoMovimiento?.toUpperCase() === 'INGRESO') return raw < 0 ? raw : Math.abs(raw);
  return raw <= 0 ? raw : -Math.abs(raw);
};

const formatComprobante = (mov: MovimientoCaja): string =>
  formatComprobanteCajaConTipo(
    mov.nombreTipoFormulario,
    mov.letra,
    mov.nroSuc,
    mov.nroComprobante,
    mov.nroFormulario
  );

interface ArqueoTipoPagoDetalleDialogProps {
  open: boolean;
  onClose: () => void;
  codigoCaja: number;
  item: ArqueoPorTipoPago | null;
  titulo: string;
  nombreCaja?: string;
  onVerMovimiento: (mov: MovimientoCaja) => void;
}

const ArqueoTipoPagoDetalleDialog: React.FC<ArqueoTipoPagoDetalleDialogProps> = ({
  open,
  onClose,
  codigoCaja,
  item,
  titulo,
  nombreCaja,
  onVerMovimiento,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [movimientos, setMovimientos] = useState<MovimientoCaja[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const cargarMovimientos = useCallback(async () => {
    if (!item?.tipoPago?.trim()) return;
    try {
      setLoading(true);
      const response = await tesoreriaCajaService.getDetalleCaja(
        codigoCaja,
        page + 1,
        rowsPerPage,
        undefined,
        undefined,
        undefined,
        item.tipoPago.trim()
      );
      setMovimientos(response.items ?? []);
      setTotalCount(response.totalCount ?? 0);
    } catch (error: unknown) {
      console.error('Error al cargar movimientos del arqueo:', error);
      enqueueSnackbar('No se pudieron cargar los movimientos.', { variant: 'error' });
      setMovimientos([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [codigoCaja, item?.tipoPago, page, rowsPerPage, enqueueSnackbar]);

  useEffect(() => {
    if (open && item) {
      void cargarMovimientos();
    }
  }, [open, item, cargarMovimientos]);

  useEffect(() => {
    if (open) {
      setPage(0);
    }
  }, [open, item?.tipoPago]);

  const handleClose = () => {
    setMovimientos([]);
    setTotalCount(0);
    setPage(0);
    onClose();
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const hayTraspasosEnListado = movimientos.some(esMovimientoTraspaso);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        Detalle — {titulo}
        {nombreCaja ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Caja {codigoCaja} — {nombreCaja}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Caja {codigoCaja}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent dividers>
        {item && (
          <Stack
            direction="row"
            spacing={2}
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 2, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1 }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary">
                Saldo neto
              </Typography>
              <Typography variant="body1" fontWeight={700}>
                {formatCurrency(item.saldo)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Ingresos
              </Typography>
              <Typography variant="body1" sx={{ color: 'success.main', fontWeight: 600 }}>
                {formatCurrency(item.totalIngresos)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Egresos
              </Typography>
              <Typography variant="body1" sx={{ color: 'error.main', fontWeight: 600 }}>
                {formatCurrency(item.totalEgresos)}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">
                Movimientos
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {item.cantidadMovimientos}
              </Typography>
            </Box>
          </Stack>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Entidad / Nombre</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>MCP</TableCell>
                    {hayTraspasosEnListado && (
                      <>
                        <TableCell sx={{ fontWeight: 600 }}>Caja origen</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Caja destino</TableCell>
                      </>
                    )}
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      Importe
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Detalle</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movimientos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={hayTraspasosEnListado ? 8 : 6} align="center" sx={{ py: 4 }}>
                        <Typography color="text.secondary">
                          No hay movimientos para este tipo de pago
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    movimientos.map((mov, idx) => {
                      const esIngreso = mov.tipoMovimiento?.toUpperCase() === 'INGRESO';
                      return (
                        <TableRow
                          key={`${mov.nroFormulario}-${idx}`}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => onVerMovimiento(mov)}
                        >
                          <TableCell>{formatDate(mov.fechaEmision)}</TableCell>
                          <TableCell>{formatComprobante(mov)}</TableCell>
                          <TableCell>
                            {mov.entidad ? `${mov.entidad} — ` : ''}
                            {mov.nombre?.trim() || '-'}
                          </TableCell>
                          <TableCell>{mov.nombreMCP?.trim() || mov.mcp?.trim() || '-'}</TableCell>
                          {hayTraspasosEnListado && (
                            <>
                              <TableCell>
                                {esMovimientoTraspaso(mov)
                                  ? formatCajaPlanillaLabel(mov.codigoCajaOrigen, mov.nombreCajaOrigen)
                                  : '—'}
                              </TableCell>
                              <TableCell>
                                {esMovimientoTraspaso(mov)
                                  ? formatCajaPlanillaLabel(mov.codigoCajaDestino, mov.nombreCajaDestino)
                                  : '—'}
                              </TableCell>
                            </>
                          )}
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 600,
                              color: esIngreso ? 'success.main' : 'error.main',
                            }}
                          >
                            {formatCurrency(signedImporteDisplay(mov))}
                          </TableCell>
                          <TableCell>{mov.detalle?.trim() || '-'}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Filas:"
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ArqueoTipoPagoDetalleDialog;
